import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Tooltip,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
  Slider,
  Chip,
  Divider
} from '@mui/material';
import {
  Person,
  School,
  SportsBasketball as ProspectIcon,
  Assignment as InformeIcon,
  PersonSearch as ScoutIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Visibility as MonitorIcon,
  Star as StarIcon,
  HelpOutline as HelpOutlineIcon
} from '@mui/icons-material';
import { usePositions } from '../utils/positions';
import { currentUserCanUseProspectFilter, PROSPECT_COMPETITION_LABELS } from '../utils/prospectUtils';
import { PLAYER_TYPE } from '../utils/playerTypes';

// Verdict options - labels will be translated in component
const getVerdictOptions = (t) => [
  { value: 'DESCARTADO', label: t('bigBoardFilters.verdictOptions.discarded'), emoji: '❌', color: '#f44336' },
  { value: 'VOLVER A VER', label: t('bigBoardFilters.verdictOptions.watchAgain'), emoji: '🔄', color: '#2196f3' },
  { value: 'DUDAS', label: t('bigBoardFilters.verdictOptions.dudas'), emoji: '❓', color: '#9c27b0' },
  { value: 'MONITORIZAR', label: t('bigBoardFilters.verdictOptions.monitor'), emoji: '👁️', color: '#ff9800' },
  { value: 'PRIORITARIO', label: t('bigBoardFilters.verdictOptions.priority'), emoji: '⭐', color: '#4caf50' }
];

// Helper function for contrast color
const getContrastColor = (hexcolor) => {
  if (!hexcolor) return '#000000';
  const r = parseInt(hexcolor.slice(1, 3), 16);
  const g = parseInt(hexcolor.slice(3, 5), 16);
  const b = parseInt(hexcolor.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

// FilterCard component
const FilterCard = ({ label, filter, onColorClick, icon }) => (
  <Card
    sx={{
      minWidth: 160,
      height: 90,
      border: '1px solid',
      borderColor: filter ? '#1976d2' : '#e0e0e0',
      backgroundColor: filter ? 'rgba(25, 118, 210, 0.04)' : 'white',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        borderColor: '#1976d2',
        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.15)'
      }
    }}
  >
    <CardContent sx={{
      pt: 0,
      px: 1.5,
      pb: 1.5,
      '&:last-child': { pb: 1.5 },
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {icon}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: filter ? '#1976d2' : '#666',
              fontSize: '1.1rem'
            }}
          >
            {label}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          <Button
            variant={filter === 'red' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => onColorClick('red')}
            sx={{
              minWidth: 55,
              height: 38,
              fontSize: '0.85rem',
              backgroundColor: filter === 'red' ? '#f44336' : 'transparent',
              borderColor: '#f44336',
              color: filter === 'red' ? 'white' : '#f44336',
              '&:hover': {
                backgroundColor: filter === 'red' ? '#d32f2f' : 'rgba(244, 67, 54, 0.08)',
                borderColor: '#f44336',
              }
            }}
          >
            0-1
          </Button>
          <Button
            variant={filter === 'green' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => onColorClick('green')}
            sx={{
              minWidth: 55,
              height: 38,
              fontSize: '0.85rem',
              backgroundColor: filter === 'green' ? '#4caf50' : 'transparent',
              borderColor: '#4caf50',
              color: filter === 'green' ? 'white' : '#4caf50',
              '&:hover': {
                backgroundColor: filter === 'green' ? '#388e3c' : 'rgba(76, 175, 80, 0.08)',
                borderColor: '#4caf50',
              }
            }}
          >
            1+
          </Button>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// PlayerTypeCard component
// L'option NCAA (joueurs rattachés à une compétition/niveau NCAA) n'est visible
// que pour super admin / admin pro.
const PlayerTypeCard = ({ playerTypeFilter, setPlayerTypeFilter, t }) => {
  const showProspects = React.useMemo(currentUserCanUseProspectFilter, []);

  return (
  <Card
    sx={{
      minWidth: 220,
      height: 90,
      border: '1px solid',
      borderColor: playerTypeFilter !== PLAYER_TYPE.ALL ? '#1976d2' : '#e0e0e0',
      backgroundColor: playerTypeFilter !== PLAYER_TYPE.ALL ? 'rgba(25, 118, 210, 0.04)' : 'white',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        borderColor: '#1976d2',
        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.15)'
      }
    }}
  >
    <CardContent sx={{
      pt: 0,
      px: 1.5,
      pb: 1.5,
      '&:last-child': { pb: 1.5 },
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: playerTypeFilter !== PLAYER_TYPE.ALL ? '#1976d2' : '#666',
              fontSize: '1.1rem'
            }}
          >
            {t('bigBoardFilters.playerType')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.75 }}>
          <ToggleButton
            value="pro"
            selected={playerTypeFilter === PLAYER_TYPE.PRO}
            onChange={() => setPlayerTypeFilter(playerTypeFilter === PLAYER_TYPE.PRO ? PLAYER_TYPE.ALL : PLAYER_TYPE.PRO)}
            sx={{
              border: '1px solid',
              borderColor: playerTypeFilter === PLAYER_TYPE.PRO ? '#ff5722' : '#e0e0e0',
              backgroundColor: playerTypeFilter === PLAYER_TYPE.PRO ? 'rgba(255, 87, 34, 0.08)' : 'transparent',
              '&:hover': {
                backgroundColor: playerTypeFilter === PLAYER_TYPE.PRO ? 'rgba(255, 87, 34, 0.15)' : 'rgba(0, 0, 0, 0.04)',
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(255, 87, 34, 0.08)',
                borderColor: '#ff5722',
                '&:hover': {
                  backgroundColor: 'rgba(255, 87, 34, 0.15)',
                }
              },
              px: 1.5,
              py: 0.75,
              minWidth: 70,
              height: 38,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
              <Person sx={{
                fontSize: 16,
                color: playerTypeFilter === PLAYER_TYPE.PRO ? '#ff5722' : '#757575'
              }} />
              <Typography variant="caption" sx={{
                color: playerTypeFilter === PLAYER_TYPE.PRO ? '#ff5722' : '#757575',
                fontWeight: playerTypeFilter === PLAYER_TYPE.PRO ? 600 : 400,
                fontSize: '0.7rem'
              }}>
                {t('bigBoardFilters.pro')}
              </Typography>
            </Box>
          </ToggleButton>

          {showProspects && (
            <Tooltip title={t('common.prospectFilterHint', { list: PROSPECT_COMPETITION_LABELS.join(', ') })}>
              <ToggleButton
                value="ncaa"
                selected={playerTypeFilter === PLAYER_TYPE.NCAA}
                onChange={() => setPlayerTypeFilter(playerTypeFilter === PLAYER_TYPE.NCAA ? PLAYER_TYPE.ALL : PLAYER_TYPE.NCAA)}
                sx={{
                  border: '1px solid',
                  borderColor: playerTypeFilter === PLAYER_TYPE.NCAA ? '#673ab7' : '#e0e0e0',
                  backgroundColor: playerTypeFilter === PLAYER_TYPE.NCAA ? 'rgba(103, 58, 183, 0.08)' : 'transparent',
                  '&:hover': {
                    backgroundColor: playerTypeFilter === PLAYER_TYPE.NCAA ? 'rgba(103, 58, 183, 0.15)' : 'rgba(0, 0, 0, 0.04)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(103, 58, 183, 0.08)',
                    borderColor: '#673ab7',
                    '&:hover': {
                      backgroundColor: 'rgba(103, 58, 183, 0.15)',
                    }
                  },
                  px: 1.5,
                  py: 0.75,
                  minWidth: 70,
                  height: 38,
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                  <ProspectIcon sx={{
                    fontSize: 16,
                    color: playerTypeFilter === PLAYER_TYPE.NCAA ? '#673ab7' : '#757575'
                  }} />
                  <Typography variant="caption" sx={{
                    color: playerTypeFilter === PLAYER_TYPE.NCAA ? '#673ab7' : '#757575',
                    fontWeight: playerTypeFilter === PLAYER_TYPE.NCAA ? 600 : 400,
                    fontSize: '0.7rem'
                  }}>
                    {t('bigBoardFilters.ncaa')}
                  </Typography>
                </Box>
              </ToggleButton>
            </Tooltip>
          )}
          <ToggleButton
            value="cantera"
            selected={playerTypeFilter === PLAYER_TYPE.CANTERA}
            onChange={() => setPlayerTypeFilter(playerTypeFilter === PLAYER_TYPE.CANTERA ? PLAYER_TYPE.ALL : PLAYER_TYPE.CANTERA)}
            sx={{
              border: '1px solid',
              borderColor: playerTypeFilter === PLAYER_TYPE.CANTERA ? '#2196f3' : '#e0e0e0',
              backgroundColor: playerTypeFilter === PLAYER_TYPE.CANTERA ? 'rgba(33, 150, 243, 0.08)' : 'transparent',
              '&:hover': {
                backgroundColor: playerTypeFilter === PLAYER_TYPE.CANTERA ? 'rgba(33, 150, 243, 0.15)' : 'rgba(0, 0, 0, 0.04)',
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(33, 150, 243, 0.08)',
                borderColor: '#2196f3',
                '&:hover': {
                  backgroundColor: 'rgba(33, 150, 243, 0.15)',
                }
              },
              px: 1.5,
              py: 0.75,
              minWidth: 70,
              height: 38,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
              <School sx={{
                fontSize: 16,
                color: playerTypeFilter === PLAYER_TYPE.CANTERA ? '#2196f3' : '#757575'
              }} />
              <Typography variant="caption" sx={{
                color: playerTypeFilter === PLAYER_TYPE.CANTERA ? '#2196f3' : '#757575',
                fontWeight: playerTypeFilter === PLAYER_TYPE.CANTERA ? 600 : 400,
                fontSize: '0.7rem'
              }}>
                {t('bigBoardFilters.cantera')}
              </Typography>
            </Box>
          </ToggleButton>

        </Box>
      </Box>
    </CardContent>
  </Card>
  );
};

// Main BigBoardFilters component
const BigBoardFilters = ({
  // Filter states
  playerTypeFilter,
  setPlayerTypeFilter,
  informeFilter,
  setInformeFilter,
  scoutFilter,
  setScoutFilter,
  verdictFilter,
  setVerdictFilter,
  nameFilter,
  setNameFilter,
  yearFilter,
  setYearFilter,
  positionFilter,
  setPositionFilter,
  groupFilter,
  setGroupFilter,
  heightFilter,
  setHeightFilter,
  ageFilter,
  setAgeFilter,
  // Additional props
  groups = [],
  groupColors = {},
  userRole
}) => {
  const { t } = useTranslation();
  const verdictOptions = getVerdictOptions(t);
  const { positionsWithKeys } = usePositions(t);

  const handleInformeClick = (color) => {
    if (informeFilter === color) {
      setInformeFilter(null);
    } else {
      setInformeFilter(color);
    }
  };

  const handleScoutClick = (color) => {
    if (scoutFilter === color) {
      setScoutFilter(null);
    } else {
      setScoutFilter(color);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 4, backgroundColor: '#fafafa' }} elevation={1}>
      {/* First row: Main filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {userRole !== 'entrenador' && (
          <Grid item xs={12} md={3}>
            <PlayerTypeCard
              playerTypeFilter={playerTypeFilter}
              setPlayerTypeFilter={setPlayerTypeFilter}
              t={t}
            />
          </Grid>
        )}
        <Grid item xs={12} md={userRole === 'entrenador' ? 12 : 9}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FilterCard
              label={t('bigBoardFilters.informe')}
              filter={informeFilter}
              onColorClick={handleInformeClick}
              icon={<InformeIcon fontSize="small" />}
            />
            <FilterCard
              label={t('bigBoardFilters.scout')}
              filter={scoutFilter}
              onColorClick={handleScoutClick}
              icon={<ScoutIcon fontSize="small" />}
            />
            <Card
              sx={{
                minWidth: 160,
                height: 90,
                border: '1px solid',
                borderColor: verdictFilter && verdictFilter.length > 0 ? '#1976d2' : '#e0e0e0',
                backgroundColor: verdictFilter && verdictFilter.length > 0 ? 'rgba(25, 118, 210, 0.04)' : 'white',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: '#1976d2',
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.15)'
                }
              }}
            >
              <CardContent sx={{
                pt: 0.5,
                px: 1.5,
                pb: 1,
                '&:last-child': { pb: 1 },
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}>
                <FormControl size="small" fullWidth variant="standard">
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: verdictFilter && verdictFilter.length > 0 ? '#1976d2' : '#666',
                      fontSize: '1.1rem',
                      textAlign: 'center',
                      mb: 1
                    }}
                  >
                    {t('bigBoardFilters.verdict')}
                  </Typography>
                  <Select
                    multiple
                    value={verdictFilter || []}
                    onChange={(e) => setVerdictFilter(e.target.value)}
                    variant="standard"
                    sx={{
                      fontSize: '0.85rem',
                      '.MuiSelect-select': {
                        padding: '4px 0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '32px'
                      }
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', gap: 0.3, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
                        {selected.length === 0 ? (
                          <Typography variant="caption" sx={{ color: '#999' }}>{t('bigBoardFilters.verdictSelect')}</Typography>
                        ) : selected.length > 2 ? (
                          <Chip
                            label={t('bigBoardFilters.verdictSelected', { count: selected.length })}
                            size="small"
                            sx={{ height: 20, fontSize: '0.75rem' }}
                          />
                        ) : (
                          selected.map((verdict) => {
                            const option = verdictOptions.find(v => v.value === verdict);
                            return (
                              <Box key={verdict} sx={{ fontSize: '14px', lineHeight: 1 }}>
                                {option?.emoji}
                              </Box>
                            );
                          })
                        )}
                      </Box>
                    )}
                  >
                    {verdictOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Checkbox checked={(verdictFilter || []).indexOf(option.value) > -1} size="small" />
                        <ListItemText primary={`${option.label} ${option.emoji}`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* Second row: Text and selection filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label={t('bigBoardFilters.searchNameTeam')}
            variant="outlined"
            size="small"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            label={t('bigBoardFilters.birthYear')}
            variant="outlined"
            size="small"
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('bigBoardFilters.position')}</InputLabel>
            <Select
              multiple
              value={positionFilter}
              onChange={(e) => setPositionFilter(e.target.value)}
              renderValue={(selected) => {
                // Traduire les clés sélectionnées pour l'affichage
                return selected.map(key => {
                  const pos = positionsWithKeys.find(p => p.key === key);
                  return pos ? pos.label : key;
                }).join(', ');
              }}
            >
              {positionsWithKeys.map((position) => (
                <MenuItem key={position.key} value={position.key}>
                  <Checkbox checked={positionFilter.indexOf(position.key) > -1} />
                  <ListItemText primary={position.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('bigBoardFilters.groups')}</InputLabel>
            <Select
              multiple
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              renderValue={(selected) => (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    maxWidth: '100%',
                    overflow: 'hidden',
                  }}
                >
                  {selected.length <= 2 ? (
                    selected.map((group) => (
                      <Chip
                        key={group}
                        label={group}
                        size="small"
                        sx={{
                          backgroundColor: groupColors[group] || 'grey',
                          color: getContrastColor(groupColors[group]),
                          maxWidth: '120px',
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                        }}
                      />
                    ))
                  ) : (
                    <Typography variant="body2">{t('bigBoardFilters.groupsCount', { count: selected.length })}</Typography>
                  )}
                </Box>
              )}
            >
              {groups.map((group) => (
                <MenuItem key={group.libelle} value={group.libelle}>
                  <Checkbox checked={groupFilter.indexOf(group.libelle) > -1} />
                  <ListItemText primary={group.libelle} />
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      backgroundColor: group.code_couleur || 'grey',
                      borderRadius: '50%',
                      ml: 1,
                    }}
                  />
                </MenuItem>
              ))}
              <MenuItem value="Sin Grupos">
                <Checkbox checked={groupFilter.indexOf('Sin Grupos') > -1} />
                <ListItemText primary={t('bigBoardFilters.noGroups')} />
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Third row: Sliders */}
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              {t('bigBoardFilters.heightRange', { min: heightFilter[0], max: heightFilter[1] })}
            </Typography>
            <Slider
              value={heightFilter}
              onChange={(e, newValue) => setHeightFilter(newValue)}
              valueLabelDisplay="auto"
              min={100}
              max={230}
              sx={{ color: '#1976d2' }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              {t('bigBoardFilters.ageRange', { min: ageFilter[0], max: ageFilter[1] })}
            </Typography>
            <Slider
              value={ageFilter}
              onChange={(e, newValue) => setAgeFilter(newValue)}
              valueLabelDisplay="auto"
              min={8}
              max={42}
              sx={{ color: '#1976d2' }}
            />
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default BigBoardFilters;
export { FilterCard, PlayerTypeCard, getVerdictOptions, getContrastColor };